import { ChangeDetectionStrategy, Component, input, output } from '@angular/core';
import { SharedModule } from '@aiwyn/shared';
import { SafeDatePipe } from '@aiwyn/shared/pipes/safe-date/safe-date.pipe';
import { CxpTask, CxpTaskIntegrationIcon, CxpTaskProgress, CxpTaskStatus, CXP_TASK_CTA_LABELS, CXP_TASK_INTEGRATION_ICONS } from '../../../../models';

export interface CxpTabOption {
  id: CxpTaskStatus;
  label: string;
  count: number;
}

@Component({
  selector: 'cxp-task-list',
  templateUrl: './task-list.component.html',
  styleUrls: ['./task-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [SharedModule, SafeDatePipe],
})
export class TaskListComponent {
  readonly tasks = input<CxpTask[]>([]);
  readonly isLoading = input(false);
  readonly tabs = input<CxpTabOption[]>([]);
  readonly activeTab = input<CxpTaskStatus>('open');
  readonly searchQuery = input<string>('');
  readonly taskSelected = output<CxpTask>();
  readonly tabChanged = output<CxpTaskStatus>();
  readonly searchQueryChanged = output<string>();

  onSearchInput(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    this.searchQueryChanged.emit(value);
  }

  /**
   * Keyboard activation for the task row. Enter and Space trigger the same
   * `taskSelected` event the click handler emits, matching native button
   * semantics. `preventDefault` on Space suppresses page scroll.
   */
  onCardKeydown(event: KeyboardEvent, task: CxpTask): void {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.taskSelected.emit(task);
    }
  }

  getCtaLabel(task: CxpTask): string {
    if (task.status === 'completed') return 'View';
    return CXP_TASK_CTA_LABELS[task.sourceStatus] ?? 'Start';
  }

  /**
   * The document, named after the signature it is for when the row was expanded per
   * signer — `Signer, Document` in the Figma. A bundle addressing the same person twice
   * yields cards whose only other difference is their status, so the signature has to be
   * part of what the card says.
   */
  getTaskTitle(task: CxpTask): string {
    return task.esignSignerName ? `${task.esignSignerName}, ${task.name}` : task.name;
  }

  /**
   * This signature's own state, which is not the bundle's: a signer who has signed is
   * done even while the document waits on someone else. Absent when the signature-request
   * read produced nothing for the row, where the card still speaks for the whole bundle.
   */
  getEsignSignerStatusLabel(task: CxpTask): string | null {
    switch (task.esignSignerStatus) {
      case 'required':
        return 'Signature required';
      case 'signed':
        return 'Signed';
      case 'awaiting-others':
        return 'Signed — waiting on others';
      default:
        return null;
    }
  }

  /**
   * Top-of-card meta line rendered as `CLIENT NAME • PROJECT NAME`. Either
   * side is dropped — along with the separating dot — when its value is
   * missing, so a client-only task reads `CLIENT NAME` with no trailing dot.
   */
  getCardMeta(task: CxpTask): string {
    return [task.clientName, task.projectName].filter(Boolean).join(' • ');
  }

  /** Completion percentage for the card's progress bar (0 when total is 0). */
  getProgressPercent(progress: CxpTaskProgress): number {
    if (progress.total <= 0) return 0;
    return Math.min(100, Math.round((progress.completed / progress.total) * 100));
  }

  getIntegrationIcon(task: CxpTask): CxpTaskIntegrationIcon | null {
    if (!task.integration) return null;
    return CXP_TASK_INTEGRATION_ICONS[task.integration.type] ?? null;
  }
}
